import { FiskalStornoAction, T, tt, type FiskalFacts } from '@agos/shared'
import { ConfirmDialog } from '@agos/ui'
import { Receipt } from 'lucide-react'
import { useState } from 'react'

/**
 * *Storno, or keep it and say why* - `FRONTEND.md` §9.620, owner R2.
 *
 * **Owner instruction, 2026-09-30**: *"miesto toho, ze ti nepovoli ulozit a musis najprv storno, sa
 * ta opyta po kliknuti na ulozenie ci chces dat storno fiskalneho alebo nie. V oboch pripadoch ti
 * objednavku ulozi"*. Both routes save; that is the whole instruction.
 *
 * **It is opened by the server's own refusal, never by a rule of this screen's.** §10A.315 answers
 * `FISKAL_DOCUMENT_LIVE` on that one 400, and §10A.292 owns `MAY_EDIT_AFTER_PRINT` - eleven names
 * from `notes` to the driver - so an edit the server is content with never reaches this dialog and a
 * copy of that allow-list here would be `agos-one-guard-spelled-twice` on a tax rule.
 *
 * **The storno route is `FiskalStornoAction` and calls no endpoint itself.** `POST /fiskal/storno`
 * re-authenticates whoever authorises the reversal - §9.442: *"the person at the till is not
 * necessarily the person entitled to reverse a registered sale, and the reversal carries their
 * name"* - so this hands that component the order and retries the **same patch** afterwards. The
 * storno has moved `fiskal_storno_at` by then, so the server's own predicate is false and the edit
 * goes through by the rule that was already there.
 *
 * **That component renders nothing without the permission, and that is left alone.** An operator who
 * may not reverse a registered sale sees only the *keep* route - which still saves the order, so the
 * owner's *"V oboch pripadoch"* holds for the choice they can make. Asking `hasPermission` here to
 * explain the absence would be a second spelling of the rule it owns.
 *
 * **The reason is required because §10A.315 refuses a blank one at the controller.** Without the
 * disabled button the operator would meet a 400 they cannot act on - and the reason is the whole
 * point of the field: it is what the `order_edit_logs` row says about why a live document was kept.
 */
export function FiskalDecisionDialog({
  order,
  saving,
  onStornoed,
  onKeep,
  onClose,
}: {
  /** The order being edited, for the storno component's own rule and its authorisation payload. */
  order: FiskalFacts & { id: number; orderNumber?: string; total?: number | string }
  /** The save in flight, so neither route can be taken twice. */
  saving: boolean
  /** The document was reversed: send the same patch again, unchanged. */
  onStornoed: () => void
  /** Keep the document and save, with the operator's reason. */
  onKeep: (reason: string) => void
  onClose: () => void
}) {
  const [reason, setReason] = useState('')

  return (
    <ConfirmDialog
      open
      variant="danger"
      icon={<Receipt className="w-5 h-5" />}
      title={tt('Objednávka má vytlačený fiškálny doklad')}
      description={tt(
        'Úprava mení sumu, ktorú doklad registruje. Stornujte doklad, alebo ho nechajte v platnosti a napíšte prečo - objednávka sa uloží v oboch prípadoch.',
      )}
      confirmLabel={tt('Uložiť bez storna')}
      cancelLabel={tt('Zrušiť')}
      loading={saving}
      confirmDisabled={reason.trim() === '' || saving}
      onConfirm={() => onKeep(reason.trim())}
      onClose={onClose}
    >
      <div className="space-y-3">
        {/* The conservative route first, because it is the one that leaves the books agreeing. */}
        <div className="flex items-center gap-2" data-fiskal-decision-storno>
          <FiskalStornoAction
            order={order}
            label={tt('Stornovať doklad a uložiť')}
            onDone={onStornoed}
          />
        </div>
        <label className="block space-y-1">
          <span className="text-[11px] font-bold text-muted-foreground">
            <T>Dôvod, prečo doklad zostáva v platnosti</T>
          </span>
          <textarea
            data-fiskal-keep-reason
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={2}
            placeholder={tt('Napríklad: doklad opravíme na konci zmeny')}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs"
          />
          {/* Said rather than implied: this is the sentence the audit row will carry, and §10A.303 is
            * the entry where an automatic write moved money between two figures and recorded neither. */}
          <span className="text-[11px] text-muted-foreground">
            <T>Zapíše sa do histórie úprav objednávky.</T>
          </span>
        </label>
      </div>
    </ConfirmDialog>
  )
}
